import { useEffect, useCallback } from 'react'

type ModifierKey = 'ctrl' | 'meta' | 'alt' | 'shift'

interface HotkeyOptions {
  /** The key to listen for (e.g., '.', 'k', 'Enter') */
  key: string
  /** Modifier keys required (ctrl, meta, alt, shift) */
  modifiers?: ModifierKey[]
  /** Callback to execute when hotkey is pressed */
  callback: () => void
  /** Whether the hotkey is enabled (default: true) */
  enabled?: boolean
  /** Prevent default browser behavior (default: true) */
  preventDefault?: boolean
}

/**
 * Hook to handle keyboard shortcuts.
 *
 * @example
 * // Cmd+. (or Ctrl+.) to toggle sidebar
 * useHotkey({
 *   key: '.',
 *   modifiers: ['meta'], // Will also work with 'ctrl' on Windows
 *   callback: () => toggleSidebar(),
 * });
 */
export function useHotkey({
  key,
  modifiers = [],
  callback,
  enabled = true,
  preventDefault = true,
}: HotkeyOptions): void {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      if (!enabled) return

      // Check if the key matches
      if (event.key !== key) return

      // Check modifier keys
      const hasCtrl = modifiers.includes('ctrl')
      const hasMeta = modifiers.includes('meta')
      const hasAlt = modifiers.includes('alt')
      const hasShift = modifiers.includes('shift')

      // For cross-platform support, treat Cmd (meta) and Ctrl as interchangeable
      // when either is specified
      const needsCtrlOrMeta = hasCtrl || hasMeta
      const hasCtrlOrMeta = event.ctrlKey || event.metaKey

      if (needsCtrlOrMeta && !hasCtrlOrMeta) return
      if (!needsCtrlOrMeta && hasCtrlOrMeta) return

      if (hasAlt !== event.altKey) return
      if (hasShift !== event.shiftKey) return

      if (preventDefault) {
        event.preventDefault()
      }

      callback()
    },
    [key, modifiers, callback, enabled, preventDefault]
  )

  useEffect(() => {
    if (!enabled) return

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown, enabled])
}
